import { ComponentType } from "react";
import { Column } from "@tanstack/react-table";
import { ListFilter } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@pushpress/pushpress-ui";

interface DropdownFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  label: string;
  options: {
    label: string;
    value: string;
    icon?: ComponentType<{ className?: string }>;
  }[];
}

export function DropdownFilter<TData, TValue>({
  column,
  options,
  label,
}: DropdownFilterProps<TData, TValue>) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          data-testid="single-dropdown-filter-button"
          className="!tw-h-8 !tw-shadow-none"
        >
          <div className="tw-flex tw-items-center tw-justify-center">
            {selectedValues?.size > 0 && (
              <Badge
                variant="secondary"
                className="tw-rounded-full tw-px-1 tw-text-xs tw-mr-2"
              >
                {selectedValues.size}
              </Badge>
            )}
            <ListFilter className="tw-h-5 tw-w-5" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="tw-w-52">
        <DropdownMenuLabel className="tw-text-sm tw-text-start tw-text-xs tw-text-neutral-600">
          {label}
        </DropdownMenuLabel>
        {options.map((option) => {
          const isSelected = selectedValues.has(option.value);
          return (
            <DropdownMenuItem
              key={option.value}
              className="tw-gap-2 tw-text-xs"
              // keep dropdown open after selection
              onSelect={(e) => e.preventDefault()}
              onClick={() => {
                if (isSelected) {
                  selectedValues.delete(option.value);
                } else {
                  selectedValues.add(option.value);
                }
                const filterValues = Array.from(selectedValues);
                column?.setFilterValue(
                  filterValues.length ? filterValues : undefined,
                );
              }}
            >
              <div className="tw-flex tw-items-center">
                <Checkbox checked={isSelected} />
              </div>
              <span className="tw-text-sm tw-text-neutral-800">
                {option.label}
              </span>
              {facets?.get(option.value) && (
                <span className="tw-ml-auto tw-flex tw-h-5 tw-w-8 tw-items-center tw-justify-center tw-font-mono tw-text-xs tw-rounded-full tw-bg-neutral-200 tw-text-neutral-800">
                  {facets.get(option.value)}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
        {selectedValues.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="tw-justify-center"
              onSelect={(e) => e.preventDefault()}
              onClick={() => column?.setFilterValue(undefined)}
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
